/* tslint:disable:max-line-length */
import {FormControl, FormGroup} from '@angular/forms';
import {
    HttpStatusCodesEnum,
    LookupModel,
    ServerResponse,
    TradingPlanResponseInterface,
    TradingPlanResponseModel, TradingPlanSearchRequestModel,
    TradingPlanSearchResponseInterface, TradingPlanSearchResponseModel,
    TradingPlanService,
    TradingPlanUpdateRequestModel
} from '@spa/api';
import {AmountMaxValidatorKey, AmountMinValidatorKey} from '@spa/validators';
import {
    PrizeWinnerDefaultOptions,
    UpdatePlanControls,
    getHourControlName,
    UpdatePlanFormControlsInterface
} from 'appAssets/descriptors';
import {PanelPlansEditMessages} from 'appAssets/messages';
import {TradingPlanFormErrorStatesEnum} from 'appEntries/panel/enums';
import {AutocompleteControl, Helper, SelectControl} from 'appShared/classes';
import {SelectOptionInterface} from 'appShared/components';
import * as moment from 'moment';
import {map, switchMap} from 'rxjs/operators';
import {of} from 'rxjs';
import {environment} from 'appEnv/environment';

export class UpdatePlanForm {
    private tradingPlanService: TradingPlanService;
    private plan: TradingPlanResponseModel;
    private planTypes: LookupModel;
    private tradingPlatforms: LookupModel;
    private planImageFile: string;
    private saveOnlyDetails: boolean;
    private saveOnlyCharacteristics: boolean;

    private detailsControls = [
        TradingPlanUpdateRequestModel.getNameSelector(),
        TradingPlanUpdateRequestModel.getIsDefaultSelector(),
        TradingPlanUpdateRequestModel.getPlanSubTypeSelector(),
        TradingPlanUpdateRequestModel.getTradingPlatformIdSelector(),
        TradingPlanUpdateRequestModel.getPhaseSelector(),
        TradingPlanUpdateRequestModel.getTradingPlanSuccessfulCompletionSelector(),
        TradingPlanUpdateRequestModel.getIndividualIdSelector(),
        TradingPlanUpdateRequestModel.getPlanPriceSelector(),
        TradingPlanUpdateRequestModel.getPlanPriceCcySelector(),
        TradingPlanUpdateRequestModel.getPlanPriceWithAutoPassSelector(),
        TradingPlanUpdateRequestModel.getPlanPriceWithAutoPassCcySelector(),
        TradingPlanUpdateRequestModel.getPreviousTradingPlanIdSelector(),
        TradingPlanUpdateRequestModel.getNextTradingPlanIdSelector(),
        TradingPlanUpdateRequestModel.getIsAutoPassAllowedSelector()
    ];

    public formGroup: FormGroup;
    public controls: UpdatePlanFormControlsInterface;

    constructor(tradingPlanService: TradingPlanService,
                plan: TradingPlanResponseModel,
                planTypes: LookupModel,
                tradingPlatforms: LookupModel,
                saveOnlyDetails: boolean,
                saveOnlyCharacteristics: boolean) {
        this.tradingPlanService = tradingPlanService;
        this.plan = plan;
        this.planTypes = planTypes;
        this.tradingPlatforms = tradingPlatforms;
        this.saveOnlyDetails = saveOnlyDetails;
        this.saveOnlyCharacteristics = saveOnlyCharacteristics;
        this.planImageFile = this.plan.imageFileStorageId;
    }

    private getDateTime(controlName: string): string {
        const period = this.formGroup.value[controlName];
        if (period == null) {
            return null;
        }
        const time = this.formGroup.value[getHourControlName(controlName)];
        const mmt = moment(period).utc(false).hour(time ?? 0).minute(0).second(0).millisecond(0);

        return mmt.toISOString();
    }

    private shouldUseFormValue(controlName: string): boolean {
        if (this.saveOnlyDetails) {
            return this.detailsControls.includes(controlName);
        }
        if (this.saveOnlyCharacteristics) {
            return !this.detailsControls.includes(controlName);
        }

        return false;
    }

    private getFieldValue<T>(controlName: string, defaultValue: T): T {
        if (!this.shouldUseFormValue(controlName)) {
            return defaultValue;
        }

        const formValue = this.formGroup.value[controlName];

        return formValue !== undefined ? formValue : defaultValue;
    }

    private getDateTimeValue(controlName: string, defaultValue: string): string {
        return this.shouldUseFormValue(controlName)
            ? this.getDateTime(controlName)
            : defaultValue;
    }

    private initializePrizeWinnerControl(control: SelectControl): void {
        const arr = [...PrizeWinnerDefaultOptions];
        if (this.plan?.prizeWinnerCount && !PrizeWinnerDefaultOptions.includes(this.plan.prizeWinnerCount)) {
            arr.push(this.plan.prizeWinnerCount);
        }
        arr.sort((a, b) => a - b);

        control.options = arr.map(item => ({
            value: item,
            label: item.toString()
        } as SelectOptionInterface));
        control.value = this.plan?.prizeWinnerCount;
    }

    private buildUpdateRequest(): TradingPlanUpdateRequestModel {
        const Model = TradingPlanUpdateRequestModel;

        return new TradingPlanUpdateRequestModel(
            this.plan.id,
            this.getFieldValue(Model.getNameSelector(), this.plan.name),
            this.getFieldValue(Model.getIsDefaultSelector(), this.plan.isDefault),
            this.getFieldValue(Model.getPlanSubTypeSelector(), this.plan.planSubType),
            this.getFieldValue(Model.getTradingPlatformIdSelector(), this.plan.tradingPlatformId),
            this.getFieldValue(Model.getPlanPriceSelector(), this.plan.planPrice),
            this.getFieldValue(Model.getPlanPriceCcySelector(), this.plan.planPriceCcy),
            this.getFieldValue(Model.getDailyHardBpSelector(), this.plan.dailyHardBp),
            this.getFieldValue(Model.getDailyHardCcySelector(), this.plan.dailyHardCcy),
            this.getFieldValue(Model.getEquityAmountSelector(), this.plan.equityAmount),
            this.getFieldValue(Model.getEquityCcySelector(), this.plan.equityCcy),
            this.getFieldValue(Model.getDailyLeverageSelector(), this.plan.dailyLeverage),
            this.getFieldValue(Model.getOvernightLeverageSelector(), this.plan.overnightLeverage),
            this.getFieldValue(Model.getTradingPeriodSelector(), this.plan.tradingPeriod),
            this.getFieldValue(Model.getExtendingOneMonthPriceSelector(), this.plan.extendingOneMonthPrice),
            this.getFieldValue(Model.getExtendingOneMonthPriceCcySelector(), this.plan.extendingOneMonthPriceCcy),
            this.getFieldValue(Model.getDailyLossLimitSelector(), this.plan.dailyLossLimit),
            this.getFieldValue(Model.getDailyLossPerPositionSelector(), this.plan.dailyLossPerPosition),
            this.getFieldValue(Model.getMaximumTotalLossSelector(), this.plan.maximumTotalLoss),
            this.plan.bypassDlPrice,
            this.plan.bypassDlPriceCcy,
            this.getFieldValue(Model.getDailyPositionAutoCloseSelector(), this.plan.dailyPositionAutoClose),
            this.plan.profitTargetAutoClose,
            this.getFieldValue(Model.getProfitTargetSelector(), this.plan.profitTarget),
            this.plan.minimumOpenLots,
            this.plan.maximumOpenLots,
            this.plan.minimumTrades,
            this.getFieldValue(Model.getMaximumInactivityDaysSelector(), this.plan.maximumInactivityDays),
            this.getFieldValue(Model.getMinimumTradingDaysSelector(), this.plan.minimumTradingDays),
            this.getFieldValue(Model.getPayoutSplitSelector(), this.plan.payoutSplit),
            this.getFieldValue(Model.getFirstWithdrawalDateSelector(), this.plan.firstWithdrawalDate),
            this.getFieldValue(Model.getSubsequentWithdrawalDelayDaysSelector(), this.plan.subsequentWithdrawalDelayDays),
            this.getFieldValue(Model.getDurationDaysSelector(), this.plan.durationDays),
            this.getFieldValue(Model.getPayoutMinTradingDaysSelector(), this.plan.payoutMinTradingDays),
            this.getFieldValue(Model.getPayoutMinAmountSelector(), this.plan.payoutMinAmount),
            this.getDateTimeValue(Model.getPlanPurchasePeriodStartSelector(), this.plan.planPurchasePeriodStart),
            this.getDateTimeValue(Model.getPlanPurchasePeriodEndSelector(), this.plan.planPurchasePeriodEnd),
            this.getDateTimeValue(Model.getPlansAccountsActivationSelector(), this.plan.plansAccountsActivation),
            this.getDateTimeValue(Model.getPlansAccountsDeactivationSelector(), this.plan.plansAccountsDeactivation),
            this.getFieldValue(Model.getPrizeTypeSelector(), this.plan.prizeType),
            this.getFieldValue(Model.getPrizeCcySelector(), this.plan.prizeCcy),
            this.getFieldValue(Model.getPrizeOfAccumulatedTableSelector(), this.plan.prizeOfAccumulatedTable),
            this.getFieldValue(Model.getPrizeMinimumSelector(), this.plan.prizeMinimum),
            this.getFieldValue(Model.getPrizeMaximumSelector(), this.plan.prizeMaximum),
            this.getFieldValue(Model.getFixPrizeSelector(), this.plan.fixPrize),
            this.getFieldValue(Model.getPrizeWinnerCountSelector(), this.plan.prizeWinnerCount),
            this.planImageFile,
            this.getFieldValue(Model.getPhaseSelector(), this.plan.phase),
            this.getFieldValue(Model.getTradingPlanSuccessfulCompletionSelector(), this.plan.tradingPlanSuccessfulCompletion),
            this.plan.individualId,
            this.getFieldValue(Model.getNextTradingPlanIdSelector(), this.plan.nextTradingPlanId),
            this.getFieldValue(Model.getMinimumProfitableDaysSelector(), this.plan.minimumProfitableDays),
            this.getFieldValue(Model.getDailyProfitPerPositionSelector(), this.plan.dailyProfitPerPosition),
            this.getFieldValue(Model.getNoGamblingRuleSelector(), this.plan.noGamblingRule),
            this.getFieldValue(Model.getMinimumTradeDurationSelector(), this.plan.minimumTradeDuration),
            this.plan.rewindDailyLossPercentage,
            this.getFieldValue(Model.getRewindDailyLossPriceSelector(), this.plan.rewindDailyLossPrice),
            this.getFieldValue(Model.getRewindDailyLossPriceCcySelector(), this.plan.rewindDailyLossPriceCcy),
            this.getFieldValue(Model.getPreviousTradingPlanIdSelector(), this.plan.previousTradingPlanId),
            this.getFieldValue(Model.getIsAutoPassAllowedSelector(), this.plan.isAutoPassAllowed),
            this.getFieldValue(Model.getPlanPriceWithAutoPassSelector(), this.plan.planPriceWithAutoPass),
            this.getFieldValue(Model.getPlanPriceWithAutoPassCcySelector(), this.plan.planPriceWithAutoPassCcy),
            this.getFieldValue(Model.getMinTradingDaysTargetSelector(), this.plan.minTradingDaysTarget),
            this.getFieldValue(Model.getMinimumTradeRangeSelector(), this.plan.minimumTradeRange),
            this.getFieldValue(Model.getRewindUsageSelector(), this.plan.rewindUsage),
            this.getFieldValue(Model.getPlanUpgradeOptionSelector(), this.plan.planUpgradeOption),
            this.getFieldValue(Model.getPlanUpgradeOptionCcySelector(), this.plan.planUpgradeOptionCcy),
            this.getFieldValue(Model.getAutoPassDeactivationPriceSelector(), this.plan.autoPassDeactivationPrice),
            this.getFieldValue(Model.getAutoPassDeactivationCcySelector(), this.plan.autoPassDeactivationCcy),
            this.getFieldValue(Model.getProgressFeePercentageSelector(), this.plan.progressFeePercentage),
            this.getFieldValue(Model.getNextPlanActivationFeePercentageSelector(), this.plan.nextPlanActivationFeePercentage),
            this.getFieldValue(Model.getFastTrackPercentageSelector(), this.plan.fastTrackPercentage),
            this.getFieldValue(Model.getPenaltyPayoutSplitSelector(), this.plan.penaltyPayoutSplit),
            this.getFieldValue(Model.getConsistencyCoefficientMinimumSelector(), this.plan.consistencyCoefficientMinimum),
            this.getFieldValue(Model.getConsistencyCoefficientSelector(), this.plan.consistencyCoefficient)
        );
    }

    private validateControl(controlName: string, messagesFunction: (controlsLabel?: string, controlLength?: number) => {
        [key: string]: string
    }): boolean {
        const shouldValidate = (this.saveOnlyDetails && this.detailsControls.includes(controlName)) ||
            (this.saveOnlyCharacteristics && !this.detailsControls.includes(controlName));

        if (!shouldValidate) {
            return true;
        }

        const control = this.controls[controlName];
        const formControl = this.formGroup.controls[control.name];

        if (!formControl || formControl.valid) {
            return true;
        }

        control.errors = Object.keys(formControl.errors).map((key: string) => {
            let controlLength = null;

            switch (key) {
                case AmountMaxValidatorKey:
                    controlLength = formControl.errors[key].max;
                    break;
                case AmountMinValidatorKey:
                    controlLength = formControl.errors[key].min;
                    break;
            }

            return messagesFunction(control.placeholder, controlLength)[key];
        });

        return false;
    }

    private validateDateRanges(messagesFunction: (controlsLabel?: string, controlLength?: number) => {
        [key: string]: string
    }): boolean {
        if (!this.saveOnlyCharacteristics) {
            return true;
        }

        let isValid = true;

        // Validate purchase period
        isValid = this.validatePurchasePeriod(messagesFunction) && isValid;

        // Validate account activation/deactivation
        isValid = this.validateAccountActivation(messagesFunction) && isValid;

        // Validate purchase and activation dates
        isValid = this.validatePurchaseAndActivation(messagesFunction) && isValid;

        return isValid;
    }

    private validatePurchasePeriod(messagesFunction: (controlsLabel?: string, controlLength?: number) => {
        [key: string]: string
    }): boolean {
        const periodEndControl = this.controls.planPurchasePeriodEnd;
        const purchasePeriodStart = moment(this.getDateTime(TradingPlanUpdateRequestModel.getPlanPurchasePeriodStartSelector())).unix();
        const purchasePeriodEnd = moment(this.getDateTime(TradingPlanUpdateRequestModel.getPlanPurchasePeriodEndSelector())).unix();

        if (purchasePeriodStart && purchasePeriodEnd && (purchasePeriodStart >= purchasePeriodEnd)) {
            periodEndControl.setError(messagesFunction(periodEndControl.placeholder)[PanelPlansEditMessages.WrongPurchasePeriodDate.key]);

            return false;
        }

        periodEndControl.removeError(messagesFunction(periodEndControl.placeholder)[PanelPlansEditMessages.WrongPurchasePeriodDate.key]);

        return true;
    }

    private validateAccountActivation(messagesFunction: (controlsLabel?: string, controlLength?: number) => {
        [key: string]: string
    }): boolean {
        const accountDeActivationDateControl = this.controls.plansAccountsActivation;
        const accountsActivationStart = moment(this.getDateTime(TradingPlanUpdateRequestModel.getPlansAccountsActivationSelector())).unix();
        const accountsDeactivation = moment(this.getDateTime(TradingPlanUpdateRequestModel.getPlansAccountsDeactivationSelector())).unix();

        if (accountsActivationStart && accountsDeactivation && (accountsActivationStart >= accountsDeactivation)) {
            accountDeActivationDateControl.setError(messagesFunction(accountDeActivationDateControl.placeholder)[PanelPlansEditMessages.WrongAccountActivationDate.key]);

            return false;
        }

        accountDeActivationDateControl.removeError(messagesFunction(accountDeActivationDateControl.placeholder)[PanelPlansEditMessages.WrongAccountActivationDate.key]);

        return true;
    }

    private validatePurchaseAndActivation(messagesFunction: (controlsLabel?: string, controlLength?: number) => {
        [key: string]: string
    }): boolean {
        const accountActivationDateControl = this.controls.plansAccountsActivation;
        const accountsActivationStart = moment(this.getDateTime(TradingPlanUpdateRequestModel.getPlansAccountsActivationSelector())).unix();
        const purchasePeriodStart = moment(this.getDateTime(TradingPlanUpdateRequestModel.getPlanPurchasePeriodStartSelector())).unix();

        if (accountsActivationStart && purchasePeriodStart && (purchasePeriodStart >= accountsActivationStart)) {
            accountActivationDateControl.setError(messagesFunction(accountActivationDateControl.placeholder)[PanelPlansEditMessages.WrongPurchaseAndActivationDate.key]);

            return false;
        }

        accountActivationDateControl.removeError(messagesFunction(accountActivationDateControl.placeholder)[PanelPlansEditMessages.WrongPurchaseAndActivationDate.key]);

        return true;
    }

    initForm(): void {
        this.formGroup = new FormGroup({});
        this.controls = UpdatePlanControls(this.plan, this.planTypes, this.tradingPlatforms);

        Object.keys(this.controls).forEach((key: string) => {
            const control = this.controls[key];

            if (control.name === TradingPlanUpdateRequestModel.getPrizeWinnerCountSelector()) {
                this.initializePrizeWinnerControl(control as SelectControl);
            } else if (control.name === TradingPlanUpdateRequestModel.getPreviousTradingPlanIdSelector() ||
                control.name === TradingPlanUpdateRequestModel.getNextTradingPlanIdSelector()) {
                const ctrl = control as AutocompleteControl;
                ctrl.asyncOptions = (symbol: string) => {
                    if (!symbol) {
                        return of([]);
                    }

                    return this.tradingPlanService
                        .search(new TradingPlanSearchRequestModel(
                            1,
                            1000,
                            TradingPlanSearchResponseModel.getReferenceIdSelector(),
                            true,
                            null,
                            null,
                            symbol,
                            null,
                            null,
                            null,
                            environment.tenant).serialize())
                        .pipe(map(response => {
                            if (response.status !== HttpStatusCodesEnum.Ok) {
                                return [];
                            } else {
                                const data = response.data as TradingPlanSearchResponseInterface[];

                                if (!data || data.length === 0) {
                                    return [];
                                }

                                return data.filter(p => p.id !== this.plan.id).map(p => {
                                    return {
                                        label: p.name + (p.phase?.trim() ? ', ' + p.phase : ''),
                                        value: p.id
                                    };
                                });
                            }
                        }));
                };
            }

            this.formGroup.addControl(control.name, new FormControl(control.value, control.validators));
        });
    }

    public updatePlanImageFileId(fileId: string | null): void {
        if (fileId !== null && fileId !== this.plan.imageFileStorageId) {
            this.planImageFile = fileId;
            this.formGroup.markAsDirty();
        }
    }

    /* eslint max-lines: off */
    createRequest(): ServerResponse<TradingPlanResponseInterface> {
        if (!this.formGroup.dirty) {
            return null;
        }

        const inputModel = this.buildUpdateRequest();

        return this.tradingPlanService.update(inputModel.serialize())
            .pipe(switchMap(registerResponse => {
                if (registerResponse.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponseObservable<TradingPlanResponseInterface>(
                        registerResponse,
                        TradingPlanFormErrorStatesEnum.UpdatePlanFailed
                    );
                }

                return Helper.createSuccessResponseObservable<TradingPlanResponseInterface>(
                    registerResponse.data as TradingPlanResponseInterface
                );
            }));
    }

    validateForm(messagesFunction: (controlsLabel?: string, controlLength?: number) => {
        [key: string]: string
    }): boolean {
        let controlsValid = true;

        Object.keys(this.controls).forEach((controlName: string) => {
            const isValid = this.validateControl(controlName, messagesFunction);
            controlsValid = controlsValid && isValid;
        });

        const dateRangesValid = this.validateDateRanges(messagesFunction);

        const consistencyCoefficient = this.formGroup.value[TradingPlanUpdateRequestModel.getConsistencyCoefficientSelector()];
        const consistencyCoefficientMinimum = this.formGroup.value[TradingPlanUpdateRequestModel.getConsistencyCoefficientMinimumSelector()];
        const consistencyCoefficientMinimumControl = this.controls.consistencyCoefficientMinimum;

        if (consistencyCoefficient && consistencyCoefficientMinimum && (consistencyCoefficientMinimum >= consistencyCoefficient)) {
            consistencyCoefficientMinimumControl.setError(messagesFunction(consistencyCoefficientMinimumControl.placeholder)[PanelPlansEditMessages.ConsistencyCoefficientShouldBeLower.key]);
            controlsValid = false;
        }else{
            consistencyCoefficientMinimumControl.removeError(messagesFunction(consistencyCoefficientMinimumControl.placeholder)[PanelPlansEditMessages.ConsistencyCoefficientShouldBeLower.key]);
        }


        return controlsValid && dateRangesValid;
    }
}
